import {
  ArrowRightIcon,
  BarChartIcon,
  BookmarkIcon,
  CheckboxIcon,
  ChevronRightIcon,
  DesktopIcon,
  EyeOpenIcon,
  EnterIcon,
  FileTextIcon,
  GridIcon,
  MagnifyingGlassIcon,
  PersonIcon,
  PlusIcon,
  Pencil2Icon,
  ReaderIcon,
  GearIcon,
} from '@radix-ui/react-icons';

import type { StudentHomeLearningSummary } from '../domain/student-home';
import { TeacherMatchIcon } from './TeacherMatchIcon';
import { StudentHomeNavigation } from './StudentHomeNavigation';
import { TeacherHomeNavigation } from './TeacherHomeNavigation';

import './role-home.css';
import './student-home.css';
import './teacher-home.css';

/** Existing lobby destinations only. This component never loads or changes business data. */
export type HomeDialog =
  | 'CREATE'
  | 'CREATE_MENU'
  | 'CURRENT_GAMES'
  | 'ENTRY'
  | 'ASSIGNED_GAMES'
  | 'HISTORY'
  | 'MY_HOMEWORK'
  | 'WATCH_GAMES'
  | 'TEACHING_MANAGE'
  | 'CLASS_GAME_VIEW'
  | 'QUESTION_BANK'
  | 'HOMEWORK_MENU'
  | 'HOMEWORK_ASSIGN'
  | 'HOMEWORK_REVIEW'
  | 'HOMEWORK_DATA'
  | 'HOMEWORK_STUDENT_RESULTS'
  | 'AI_MANAGEMENT';

interface HomeAction {
  readonly destination: HomeDialog | 'FOX_KIFU' | 'CLASSROOM_LECTURE' | 'EBOOKS';
  readonly label: string;
  readonly description?: string;
  readonly callToAction?: string;
  readonly appearance?: 'primary' | 'warm' | 'compact';
}

const studentGames: readonly HomeAction[] = [
  {
    destination: 'ASSIGNED_GAMES',
    label: '班级对弈',
    description: '查看老师安排的对局',
    callToAction: '查看对局',
    appearance: 'warm',
  },
  { destination: 'CREATE', label: '创建新对局', appearance: 'compact' },
  { destination: 'ENTRY', label: '加入/恢复对局', appearance: 'compact' },
  { destination: 'WATCH_GAMES', label: '观战', appearance: 'compact' },
];

const studentRecords: readonly HomeAction[] = [
  { destination: 'HISTORY', label: '我的棋谱', description: '重温对局里的好想法' },
  { destination: 'FOX_KIFU', label: '野狐棋谱', description: '跟着高手，发现妙招' },
];

const teacherTeaching: readonly HomeAction[] = [
  {
    destination: 'HOMEWORK_REVIEW',
    label: '批改作业',
    description: '查看作业与批改进度',
    callToAction: '选择作业',
    appearance: 'primary',
  },
  {
    destination: 'HOMEWORK_ASSIGN',
    label: '布置作业',
    description: '从题库选题，布置给学生',
    callToAction: '开始布置',
  },
];

const teacherCreateActions: readonly HomeAction[] = [
  { destination: 'CREATE_MENU', label: '创建对局', description: '普通对局 · 学生对局 · 指导对弈' },
  { destination: 'ENTRY', label: '加入或恢复对局', appearance: 'compact' },
];

const teacherHomework: HomeAction = {
  destination: 'HOMEWORK_MENU',
  label: '全部作业管理',
};

const teacherHomeworkActions: readonly HomeAction[] = [
  { destination: 'HOMEWORK_DATA', label: '作业数据', appearance: 'compact' },
  { destination: 'HOMEWORK_STUDENT_RESULTS', label: '学生成绩', appearance: 'compact' },
];

const teacherClassroom: HomeAction = {
  destination: 'CLASSROOM_LECTURE',
  label: '大盘讲解',
  description: '摆棋讲解 · 课堂笔记 · 棋谱演示',
  callToAction: '进入课堂',
};

const teacherClassGameData: HomeAction = {
  destination: 'CLASS_GAME_VIEW',
  label: '学生对局',
  description: '观摩与复盘',
  callToAction: '查看对局',
};

const teacherManagement: readonly HomeAction[] = [
  {
    destination: 'EBOOKS',
    label: '我的教材',
    description: '查看与管理教学教材',
  },
  { destination: 'QUESTION_BANK', label: '题库管理', description: '分类、导入与编辑题目' },
  {
    destination: 'TEACHING_MANAGE',
    label: '学生信息',
    description: '查询学生与调整班级',
  },
  {
    destination: 'AI_MANAGEMENT',
    label: 'AI管理',
    description: '账号、算力与权限',
  },
];

function teacherIcon(destination: HomeAction['destination']) {
  switch (destination) {
    case 'CREATE_MENU':
      return <TeacherMatchIcon />;
    case 'ENTRY':
      return <EnterIcon aria-hidden="true" />;
    case 'HOMEWORK_DATA':
      return <BarChartIcon aria-hidden="true" />;
    case 'CLASS_GAME_VIEW':
      return <PersonIcon aria-hidden="true" />;
    case 'CLASSROOM_LECTURE':
      return <DesktopIcon aria-hidden="true" />;
    case 'HOMEWORK_REVIEW':
      return <CheckboxIcon aria-hidden="true" />;
    case 'HOMEWORK_ASSIGN':
      return <Pencil2Icon aria-hidden="true" />;
    case 'HOMEWORK_STUDENT_RESULTS':
    case 'TEACHING_MANAGE':
      return <PersonIcon aria-hidden="true" />;
    case 'AI_MANAGEMENT':
      return <GearIcon aria-hidden="true" />;
    case 'QUESTION_BANK':
      return <GridIcon aria-hidden="true" />;
    case 'EBOOKS':
      return <ReaderIcon aria-hidden="true" />;
    default:
      return <FileTextIcon aria-hidden="true" />;
  }
}

export function RoleHome({
  role,
  displayName,
  error,
  recovering,
  onOpenDialog,
  onOpenFoxKifu,
  onOpenClassroomLecture,
  onOpenStudentPractice,
  studentLearning = { status: 'loading' },
  openingHomework = false,
  onOpenEbooks,
  onContinueHomework,
  onOpenHomeworkFeedback,
  onRetryStudentLearning,
  recoveringGameDisabled = false,
  onLogout,
}: {
  readonly role: 'STUDENT' | 'TEACHER';
  readonly displayName: string;
  readonly error: string | null;
  readonly recovering: boolean;
  readonly onOpenDialog: (dialog: HomeDialog) => void;
  readonly onOpenFoxKifu: () => void;
  readonly onOpenClassroomLecture: () => void;
  readonly onOpenStudentPractice?: (() => void) | undefined;
  readonly studentLearning?: StudentHomeLearningSummary | undefined;
  readonly openingHomework?: boolean | undefined;
  readonly onOpenEbooks?: (() => void) | undefined;
  readonly onContinueHomework?: ((assignmentId: string) => void) | undefined;
  readonly onOpenHomeworkFeedback?: (() => void) | undefined;
  readonly onRetryStudentLearning?: (() => void) | undefined;
  readonly recoveringGameDisabled?: boolean | undefined;
  readonly onLogout?: (() => void) | undefined;
}) {
  const assignment = studentLearning.status === 'ready' ? studentLearning.assignment : null;
  const progress =
    assignment && assignment.totalCount > 0
      ? Math.round((assignment.submittedCount / assignment.totalCount) * 100)
      : 0;
  const activate = (action: HomeAction) => {
    if (action.destination === 'EBOOKS') {
      onOpenEbooks?.();
      return;
    }
    if (action.destination === 'FOX_KIFU') {
      onOpenFoxKifu();
      return;
    }
    if (action.destination === 'CLASSROOM_LECTURE') {
      onOpenClassroomLecture();
      return;
    }
    onOpenDialog(action.destination);
  };

  const renderTeacherAction = (
    action: HomeAction,
    variant: 'task' | 'row' | 'resource' = 'task',
  ) => (
    <button
      key={action.destination}
      className={`teacher-home__action teacher-home__action--${variant} teacher-home__action--${action.destination.toLowerCase()}`}
      type="button"
      aria-label={action.label}
      onClick={() => activate(action)}
    >
      {action.destination === 'CLASSROOM_LECTURE' ? (
        <img
          className="teacher-home__lecture-art"
          src="/assets/teacher-home/atelier-lecture-v1.webp"
          alt=""
          width="1600"
          height="400"
          decoding="async"
        />
      ) : null}
      <span className="teacher-home__icon">{teacherIcon(action.destination)}</span>
      <span className="teacher-home__copy">
        <strong>{action.label}</strong>
        {action.description && variant !== 'resource' ? <span>{action.description}</span> : null}
      </span>
      {action.destination === 'CLASSROOM_LECTURE' ? (
        <span className="teacher-home__motto" aria-hidden="true">
          以棋育人
          <br />
          以思启智
        </span>
      ) : null}
      <span className={variant === 'task' ? 'teacher-home__cta' : 'teacher-home__arrow'}>
        {variant === 'task' ? action.callToAction : null}
        <ArrowRightIcon aria-hidden="true" />
      </span>
    </button>
  );

  const renderTeacherShortcut = (action: HomeAction) => (
    <button
      key={action.destination}
      className={`teacher-home__shortcut teacher-home__shortcut--${action.destination.toLowerCase()}`}
      type="button"
      aria-label={action.label}
      onClick={() => activate(action)}
    >
      <span className="teacher-home__shortcut-icon">{teacherIcon(action.destination)}</span>
      <span>{action.label}</span>
      <ArrowRightIcon aria-hidden="true" />
    </button>
  );

  return (
    <div className={`role-home role-home--${role.toLowerCase()}`}>
      {role === 'TEACHER' ? (
        <TeacherHomeNavigation displayName={displayName} onLogout={onLogout}>
          {teacherManagement.map((action) => renderTeacherAction(action, 'resource'))}
        </TeacherHomeNavigation>
      ) : null}
      {role === 'STUDENT' ? (
        <>
          <StudentHomeNavigation
            disabled={openingHomework}
            onOpenEbooks={onOpenEbooks}
            onOpenPractice={onOpenStudentPractice}
          />
          <header className="student-home__heading" id="student-home-heading">
            <h1>
              <span>小小棋盘，</span>
              <span className="student-home__discovery">
                大大发现！
                <img
                  className="student-home__title-accents"
                  src="/assets/student-home/title-accents-v1.png"
                  alt=""
                  width="2172"
                  height="724"
                  decoding="async"
                />
              </span>
            </h1>
            <p>慢慢想，大胆试，每一个好想法都值得被看见。</p>
          </header>
        </>
      ) : (
        <header className="teacher-home__heading" id="teacher-home-heading" tabIndex={-1}>
          <div>
            <h1>教师工作台</h1>
            <p>让每一堂课，清晰而有趣</p>
          </div>
          <span className="teacher-home__identity">{displayName}</span>
        </header>
      )}
      {error === null ? null : (
        <p className="role-home__feedback is-error" role="alert">
          {error}
        </p>
      )}
      {recovering ? (
        <p className="role-home__feedback" role="status">
          正在查找并恢复对局，请稍候…
        </p>
      ) : null}
      {role === 'STUDENT' ? (
        <div className="student-home__workspace">
          <section
            className="student-home__homework student-home__surface"
            aria-labelledby="student-homework-title"
            aria-busy={studentLearning.status === 'loading'}
          >
            <img
              className="student-home__scene student-home__scene--homework"
              src="/assets/student-home/stone-pencil-cloud-v1.webp"
              alt=""
              width="1254"
              height="1254"
              decoding="async"
            />
            <div className="student-home__homework-copy">
              <div className="student-home__card-heading">
                <span className="student-home__icon">
                  <BookmarkIcon aria-hidden="true" />
                </span>
                <div className="student-home__heading-copy">
                  <div className="student-home__title-row">
                    <h2 id="student-homework-title">我的作业</h2>
                    {assignment ? (
                      <span className="student-home__status">{assignment.statusLabel}</span>
                    ) : null}
                  </div>
                  <p>小小练习，大大进步</p>
                </div>
              </div>
              <div className="student-home__assignment" aria-live="polite">
                <h3>
                  {assignment?.title ??
                    (studentLearning.status === 'loading'
                      ? '正在查看你的作业…'
                      : studentLearning.status === 'error'
                        ? '作业暂时未能加载'
                        : '还没有收到作业')}
                </h3>
                {assignment ? (
                  <>
                    <p>
                      已提交{' '}
                      <strong>
                        {assignment.submittedCount} / {assignment.totalCount}
                      </strong>{' '}
                      题
                    </p>
                    <div className="student-home__progress">
                      <progress
                        value={assignment.submittedCount}
                        max={Math.max(1, assignment.totalCount)}
                        aria-label="作业提交进度"
                      />
                      <span>{progress}%</span>
                    </div>
                  </>
                ) : (
                  <p className="student-home__state-copy">
                    {studentLearning.status === 'error'
                      ? studentLearning.message
                      : studentLearning.status === 'loading'
                        ? '稍等一下，正在同步最新进度。'
                        : '老师布置后会显示在这里，也可以先读书或对弈。'}
                  </p>
                )}
              </div>
              <div className="student-home__homework-actions">
                {assignment?.canContinue && onContinueHomework ? (
                  <button
                    className="student-home__cta"
                    type="button"
                    disabled={openingHomework}
                    aria-busy={openingHomework}
                    aria-label={assignment.submittedCount > 0 ? '继续作答' : '开始作答'}
                    onClick={() => onContinueHomework(assignment.assignmentId)}
                  >
                    {openingHomework
                      ? '正在打开…'
                      : assignment.submittedCount > 0
                        ? '继续作答'
                        : '开始作答'}
                    <ArrowRightIcon aria-hidden="true" />
                  </button>
                ) : studentLearning.status === 'error' && onRetryStudentLearning ? (
                  <button
                    className="student-home__cta"
                    type="button"
                    aria-label="重新加载作业"
                    disabled={openingHomework}
                    onClick={onRetryStudentLearning}
                  >
                    重新加载
                    <ArrowRightIcon aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    className="student-home__cta"
                    type="button"
                    aria-label="我的作业"
                    disabled={openingHomework}
                    onClick={() => onOpenDialog('MY_HOMEWORK')}
                  >
                    查看作业
                    <ArrowRightIcon aria-hidden="true" />
                  </button>
                )}
                {assignment?.canContinue || studentLearning.status === 'error' ? (
                  <button
                    className="student-home__text-link"
                    type="button"
                    aria-label="全部作业"
                    disabled={openingHomework}
                    onClick={() => onOpenDialog('MY_HOMEWORK')}
                  >
                    全部作业
                    <ChevronRightIcon aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </div>
          </section>
          <section
            className="student-home__games student-home__surface"
            id="student-home-games"
            aria-labelledby="student-games-title"
            tabIndex={-1}
          >
            <div className="student-home__card-heading">
              <span className="student-home__icon student-home__icon--green">
                <PersonIcon aria-hidden="true" />
              </span>
              <div className="student-home__heading-copy">
                <h2 id="student-games-title">棋友对弈</h2>
                <p>和小伙伴切磋，发现新的好棋</p>
              </div>
            </div>
            <div className="student-home__games-body">
              <img
                className="student-home__games-art"
                src="/assets/student-home/board-cloud-v1.webp"
                alt=""
                width="900"
                height="720"
                decoding="async"
              />
              <div className="student-home__games-actions">
                <button
                  className="student-home__class-entry"
                  type="button"
                  aria-label="班级对弈"
                  disabled={openingHomework}
                  onClick={() => onOpenDialog('ASSIGNED_GAMES')}
                >
                  <span className="student-home__icon student-home__icon--green">
                    <PersonIcon aria-hidden="true" />
                  </span>
                  <span>
                    <strong>班级对弈</strong>
                    <small>老师安排的对局，在这里集合</small>
                  </span>
                  <ChevronRightIcon aria-hidden="true" />
                </button>
                <div className="student-home__quick-actions">
                  {studentGames.slice(1).map((action) => (
                    <button
                      key={action.destination}
                      type="button"
                      aria-label={action.label}
                      disabled={openingHomework}
                      onClick={() => activate(action)}
                    >
                      {action.destination === 'CREATE' ? (
                        <PlusIcon aria-hidden="true" />
                      ) : action.destination === 'ENTRY' ? (
                        <EnterIcon aria-hidden="true" />
                      ) : (
                        <EyeOpenIcon aria-hidden="true" />
                      )}
                      <span>{action.destination === 'ENTRY' ? '加入或恢复' : action.label}</span>
                    </button>
                  ))}
                </div>
                <button
                  className="student-home__recover"
                  type="button"
                  aria-label="我的对局列表"
                  disabled={openingHomework || recoveringGameDisabled}
                  onClick={() => onOpenDialog('CURRENT_GAMES')}
                >
                  我的对局列表
                  <ArrowRightIcon aria-hidden="true" />
                </button>
              </div>
            </div>
          </section>
          {onOpenEbooks ? (
            <section
              className="student-home__ebooks student-home__surface"
              aria-labelledby="student-ebooks-title"
            >
              <div className="student-home__card-heading">
                <span className="student-home__icon student-home__icon--green">
                  <ReaderIcon aria-hidden="true" />
                </span>
                <div className="student-home__heading-copy">
                  <h2 id="student-ebooks-title">我的小书架</h2>
                  <p>一本棋书，一个新发现</p>
                </div>
              </div>
              <div className="student-home__ebooks-body">
                <img
                  className="student-home__books-art"
                  src="/assets/student-home/books-cloud-v1.webp"
                  alt=""
                  width="900"
                  height="675"
                  decoding="async"
                />
                <div className="student-home__books-copy">
                  <p className="student-home__book-label">读一读，想一想</p>
                  <button
                    className="student-home__books-entry"
                    type="button"
                    aria-label="我的电子书"
                    disabled={openingHomework}
                    onClick={onOpenEbooks}
                  >
                    进入书架
                    <ArrowRightIcon aria-hidden="true" />
                  </button>
                </div>
              </div>
            </section>
          ) : null}
          <section
            className="student-home__review student-home__surface"
            aria-labelledby="student-review-title"
          >
            <div className="student-home__card-heading">
              <span className="student-home__icon">
                <ReaderIcon aria-hidden="true" />
              </span>
              <div className="student-home__heading-copy">
                <h2 id="student-review-title">复习与发现</h2>
                <p>再看一遍，原来我又进步了</p>
              </div>
            </div>
            <div className="student-home__review-links">
              <button
                type="button"
                aria-label="查看作业批改"
                disabled={openingHomework}
                onClick={onOpenHomeworkFeedback ?? (() => onOpenDialog('MY_HOMEWORK'))}
              >
                <span className="student-home__icon student-home__icon--blue">
                  <FileTextIcon aria-hidden="true" />
                </span>
                <span>
                  <strong>查看作业批改</strong>
                  <small>看看老师的小提醒</small>
                </span>
                <ChevronRightIcon aria-hidden="true" />
              </button>
              {studentRecords.map((action) => (
                <button
                  key={action.destination}
                  type="button"
                  aria-label={action.label}
                  disabled={openingHomework}
                  onClick={() => activate(action)}
                >
                  <span
                    className={`student-home__icon ${action.destination === 'HISTORY' ? 'student-home__icon--warm' : 'student-home__icon--green'}`}
                  >
                    {action.destination === 'HISTORY' ? (
                      <MagnifyingGlassIcon aria-hidden="true" />
                    ) : (
                      <FileTextIcon aria-hidden="true" />
                    )}
                  </span>
                  <span>
                    <strong>{action.label}</strong>
                    <small>{action.description}</small>
                  </span>
                  <ChevronRightIcon aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        </div>
      ) : (
        <>
          <div className="teacher-home__workspace">
            {renderTeacherAction(teacherClassroom)}
            <section
              className="teacher-home__panel teacher-home__homework"
              aria-labelledby="teacher-homework-title"
            >
              <h2 id="teacher-homework-title">作业与成绩</h2>
              <div className="teacher-home__rows">
                {teacherTeaching.map((action) => renderTeacherAction(action, 'row'))}
              </div>
              <div className="teacher-home__shortcuts">
                {[...teacherHomeworkActions, teacherHomework].map(renderTeacherShortcut)}
              </div>
            </section>
            <section
              className="teacher-home__panel teacher-home__games"
              aria-labelledby="teacher-games-title"
            >
              <h2 id="teacher-games-title">对局教学</h2>
              <div className="teacher-home__rows">
                {renderTeacherAction(teacherClassGameData, 'row')}
                {teacherCreateActions.map((action) => renderTeacherAction(action, 'row'))}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
